/**
 * For reference:
 * - https://github.com/maeneak/mapbox-texture-layer
 * - https://github.com/naivemap/mapbox-gl-layers/tree/main/packages/mapbox-gl-image-layer
 */
import * as twgl from 'twgl.js';

import rasterVertexSource from './shaders/raster.vert';
import rasterFragmentSource from './shaders/raster.frag';
import maskVertexSource from './shaders/mask.vert';
import maskFragmentSource from './shaders/mask.frag';
import { getBufferInfo, getMaskBufferInfo } from './helpers';
import { MAPBOX_SOURCECACHE_LAYER_PREFIX } from '../../../constants/map';
import { multipolygonToPolygons } from '../../geojson';

export default class RasterLayerWithClipping {
  constructor({
    id,
    spec,
    opacity = 1,
    resampling = 'linear',
    maskPolygons = null, // maskPolygons = array of geojson polygon features
    hueTarget = -1,
    hueThreshold = 0,
    saturationThreshold = 0,
    fadeFactor = 0.75,
  }) {
    this.map = null;
    this.gl = null;
    this.type = 'custom';
    this.tileSource = null;
    this.programInfo = null;

    this.id = id;
    this.source = `${this.id}Source`;
    this.tileJson = spec;
    this.opacity = opacity;
    this.maskPolygons = maskPolygons;
    this.resampling = resampling;
    this.hueTarget = hueTarget;
    this.hueThreshold = hueThreshold;
    this.saturationThreshold = saturationThreshold;
    this.fadeFactor = fadeFactor;
  }

  onAdd(map, gl) {
    this.map = map;
    this.gl = gl;

    this.programInfo = twgl.createProgramInfo(gl, [
      rasterVertexSource,
      rasterFragmentSource,
    ]);

    this.maskProgramInfo = twgl.createProgramInfo(gl, [
      maskVertexSource,
      maskFragmentSource,
    ]);
    map.addSource(this.source, this.tileJson);

    this.tileSource = this.map.getSource(this.source);

    // eslint-disable-next-line no-underscore-dangle
    this.sourceCache = this.map.painter.style._sourceCaches[
      `${MAPBOX_SOURCECACHE_LAYER_PREFIX}${this.source}`
    ];

    // This is a hack to make mapbox mark the sourceCache as 'used' so it will initialise tiles.
    this.map.style._layers[this.id].source = this.source; // eslint-disable-line no-underscore-dangle

    this.setupLayer();
  }

  setupLayer() {
    try {
      this.bufferInfo = getBufferInfo(this.gl);
      this.setupMask();
    } catch (err) {
      console.error(err);
    }
  }

  setupMask() {
    this.maskBufferInfo = getMaskBufferInfo(this.gl, this.maskPolygons ?? []);
  }

  onRemove(map, gl) {
    map.removeSource(this.source);

    if (this.programInfo) gl.deleteProgram(this.programInfo.program);
  }

  updateOpacity(opacity) {
    this.opacity = opacity;
  }

  updateHueTarget(hueTarget) {
    this.hueTarget = hueTarget;
  }

  updateHueThreshold(hueThreshold) {
    this.hueThreshold = hueThreshold;
  }

  updateSaturationThreshold(saturationThreshold) {
    this.saturationThreshold = saturationThreshold;
  }

  updateFadeFactor(fadeFactor) {
    this.fadeFactor = fadeFactor;
  }

  updateMask(features) {
    if (!this.gl || !this.map) return;

    // Convert MultiPolygon features into multiple Polygon features
    const polygons = features.flatMap((feature) => {
      if (feature.geometry.type === 'MultiPolygon') {
        return multipolygonToPolygons(feature);
      }
      return feature;
    });

    this.maskPolygons = polygons;

    this.setupMask();
  }

  render(gl, matrix) {
    if (!this.programInfo) return;

    // blend
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const tiles = this.sourceCache
      .getVisibleCoordinates()
      .map((tileid) => this.sourceCache.getTile(tileid));

    tiles.forEach((tile) => {
      if (!tile.texture) return;

      if (this.maskProgramInfo && this.maskBufferInfo) {
        // MASK
        gl.useProgram(this.maskProgramInfo.program);

        // stencil
        gl.enable(gl.STENCIL_TEST);
        gl.stencilFunc(gl.ALWAYS, 1, 0xff);
        gl.stencilOp(gl.REPLACE, gl.REPLACE, gl.REPLACE);
        gl.stencilMask(0xff);
        gl.clear(gl.STENCIL_BUFFER_BIT);

        // matrix
        twgl.setUniforms(this.maskProgramInfo, { uMatrix: matrix });

        // pos & indices
        twgl.setBuffersAndAttributes(
          gl,
          this.maskProgramInfo,
          this.maskBufferInfo,
        );

        // draw
        twgl.drawBufferInfo(gl, this.maskBufferInfo, gl.TRIANGLES);
      }

      // RASTER
      gl.useProgram(this.programInfo.program);

      // stencil
      if (this.maskProgramInfo?.program) {
        gl.stencilFunc(gl.EQUAL, 1, 0xff);
        gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
      }

      // texture
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tile.texture.texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

      // texture sampling (linear or nearest)
      const textureFilter =
        this.resampling === 'linear' ? gl.LINEAR : gl.NEAREST;
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, textureFilter);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, textureFilter);

      // draw
      twgl.setUniforms(this.programInfo, {
        uMatrix: tile.tileID.projMatrix,
        uTexture: tile.texture.texture,
        uOpacity: this.opacity,
        uHueTarget: this.hueTarget,
        uHueThreshold: this.hueThreshold,
        uSaturationThreshold: this.saturationThreshold,
        uFadeFactor: this.fadeFactor,
      });
      twgl.setBuffersAndAttributes(gl, this.programInfo, this.bufferInfo);
      twgl.drawBufferInfo(gl, this.bufferInfo, gl.TRIANGLES);

      // cleanup
      gl.clear(gl.STENCIL_BUFFER_BIT);
      gl.disable(gl.STENCIL_TEST);
    });
  }
}
